import countries from './data/countries.json';
import axios from 'redaxios';

export function getFlagEmoji(countryCode: string) {
  const codePoints = countryCode
    .replace('en', 'gb')
    .toUpperCase()
    .split('')
    .map((char) => 127397 + (char as any).charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export function getCountryName(countryCode: string, locale: 'fr' | 'en' = 'fr') {
  const country = countries.find((country) => country._id === countryCode);
  return country?._i18n[locale].name;
}

export interface SuggestionResponse {
  type: 'FeatureCollection';
  version: string;
  features: SuggestionItem[];
  attribution: string;
  licence: string;
  query: string;
  limit: number;
  filters?: {
    type: string;
  };
}

export interface SuggestionItem {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: {
    label: string;
    score: number;
    housenumber: string;
    id: string;
    type: string;
    name: string;
    postcode: string;
    citycode: string;
    x: number;
    y: number;
    city: string;
    district?: string;
    context: string;
    importance: number;
    street: string;
  };
}

export interface Root {
  type: string;
  features: Feature[];
  query: Query;
}

export interface Feature {
  type: string;
  properties: Properties;
  geometry: Geometry;
  bbox: number[];
}

export interface Properties {
  datasource: Datasource;
  country: string;
  country_code: string;
  state: string;
  county: string;
  city: string;
  lon: number;
  lat: number;
  state_code?: string;
  result_type: string;
  formatted: string;
  address_line1: string;
  address_line2: string;
  category: string;
  timezone: Timezone;
  plus_code: string;
  rank: Rank;
  place_id: string;
  municipality?: string;
  postcode?: string;
  name?: string;
  village?: string;
  plus_code_short?: string;
  state_district?: string;
}

export interface Datasource {
  sourcename: string;
  attribution: string;
  license: string;
  url: string;
}

export interface Timezone {
  name: string;
  offset_STD: string;
  offset_STD_seconds: number;
  offset_DST: string;
  offset_DST_seconds: number;
  abbreviation_STD: string;
  abbreviation_DST: string;
}

export interface Rank {
  importance?: number;
  confidence: number;
  confidence_city_level: number;
  match_type: string;
}

export interface Geometry {
  type: string;
  coordinates: number[];
}

export interface Query {
  text: string;
  parsed: Parsed;
}

export interface Parsed {
  city: string;
  expected_type: string;
}

export type Address = {
  geometry: SuggestionResponse['features'][0]['geometry'];
  label: string;
};

export type LocationIQResponse = LocationIQ[];

export interface LocationIQ {
  place_id: string;
  osm_id: string;
  osm_type: string;
  licence: string;
  lat: string;
  lon: string;
  boundingbox: string[];
  class: string;
  type: string;
  display_name: string;
  display_place: string;
  display_address: string;
  address: LocationIQAddress;
}

export interface LocationIQAddress {
  name: string;
  county?: string;
  state?: string;
  postcode?: string;
  country: string;
  country_code: string;
  road?: string;
  city?: string;
  house_number?: string;
  suburb?: string;
}

const baseBanURL = process.env.NEXT_PUBLIC_BAN_API_BASE_URL;
const baseGeoapifyApiKey = process.env.GEOAPIFY_APIKEY;
const locationIQAccessToken = process.env.LOCATIONIQ_ACCESS_TOKEN;

export const fetchWithGeoapify = async (queryParams: { [key: string]: string }) => {
  const url = `https://api.geoapify.com/v1/geocode/autocomplete?&apiKey=${baseGeoapifyApiKey}&${new URLSearchParams(queryParams).toString()}`;

  const { data: suggestions } = await axios.get<Root>(url);

  return suggestions.features
    .filter((address) => address.geometry.type === 'Point')
    .map((feature) => ({
      geometry: {
        type: 'Point', // Ensure the geometry type is always 'Point' to match the Address type
        coordinates: feature.geometry.coordinates as [number, number],
      },
      label: feature.properties.formatted,
    }));
};

export const fetchWithLocationIq = async (queryParams: { [key: string]: string }) => {
  const url = `https://eu1.locationiq.com/v1/autocomplete?&key=${locationIQAccessToken}&${new URLSearchParams(queryParams).toString()}`;

  const { data: suggestions } = await axios.get<LocationIQResponse>(url);

  return suggestions.map((feature) => ({
    geometry: {
      type: 'Point' as 'Point', // Ensure the geometry type is always 'Point' to match the Address type
      coordinates: [+feature.lon, +feature.lat] as [number, number],
    },
    label: [
      feature.display_place,
      [feature.address.postcode, feature.address.city !== feature.display_place && feature.address.city, getFlagEmoji(feature.address.country_code)]
        .filter(Boolean)
        .join(' '),
    ]
      .filter(Boolean)
      .join(' - '),
  }));
};

export const fetchAddress = async (query: { q: string; limit: string; countryIds: string }) => {
  const { countryIds: countryIdsString, ...queryParams } = query;
  let addresses: Address[] = [];

  const countryIds = countryIdsString?.split(',');
  const onlyCountryId = countryIds?.length === 1 && countryIds[0];

  if (onlyCountryId && onlyCountryId === 'FR') {
    const url = `${baseBanURL}?${new URLSearchParams({
      q: queryParams.q,
      limit: queryParams.limit,
      type: 'municipality',
    }).toString()}`;
    const { data: suggestions } = await axios.get<SuggestionResponse>(url);

    addresses = suggestions.features.map((feature) => ({
      geometry: feature.geometry,
      label: [
        feature.properties.label,
        [feature.properties.postcode, feature.properties.city !== feature.properties.label && feature.properties.city].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(' - '),
    }));
  } else {
    addresses = await fetchWithLocationIq({
      'accept-language': 'fr',
      q: queryParams.q,
      tag: 'place:*',
      limit: queryParams.limit,
      dedupe: '1',
      ...(countryIds ? { countrycodes: countryIdsString.toLowerCase() } : {}),
    });
  }
  return addresses;
};
